import React from 'react';
import { Box, Flex, FormLabel, Text } from '@chakra-ui/react';
import { PillarSelect, ThemeSelect } from './CatalogSelect';
import GriMultiSelect from './GriMultiSelect';
import MedidasGriComoFormulario from './MedidasGriComoFormulario';

export const MENSAGEM_TEMA_OBRIGATORIO =
  'O campo Tema é obrigatório quando um Pilar é selecionado';

/**
 * EG-41 — CA03: obrigatoriedade condicional.
 *
 * Pilar é opcional; escolhido o pilar, o tema passa a ser obrigatório. A regra
 * vive aqui, e não dentro de cada formulário, porque a mesma dupla aparece na
 * criação de questão, no modal de edição e na classificação em massa — três
 * cópias divergiriam no primeiro ajuste.
 *
 * Tema sem pilar é aceito de propósito: o tema já pertence a um pilar no
 * catálogo (theme.id_pillar), então o backend deriva a classificação completa.
 */
export const validarClassificacao = (valor = {}) =>
  valor?.id_pillar && !valor?.id_theme ? MENSAGEM_TEMA_OBRIGATORIO : null;

/**
 * Renderiza como SEÇÃO delimitada, e não como mais dois campos soltos.
 *
 * O formulário da questão é uma grade de colunas fixas para código, título e
 * tipo. Enfiar os seletores ali dentro estourava a grade e jogava os campos
 * para fora da caixa branca. Além do defeito visual, classificação é uma
 * decisão de natureza diferente das demais — agrupá-la deixa isso explícito e
 * dá lugar previsível para o checkbox de propagação nas telas que o têm.
 */
const ClassificacaoQuestao = ({
  valor = {},
  onChange,
  erro,
  children,
  griSoma = false,
}) => {
  const idPillar = valor?.id_pillar ?? null;
  const idTheme = valor?.id_theme ?? null;
  const griIds = valor?.gri_ids ?? [];

  const trocarPilar = (novoPilar) => {
    // Trocar o pilar SEMPRE zera o tema. O tema anterior pertencia ao pilar
    // anterior; mantê-lo produziria um par incoerente que o backend recusa e
    // que o gestor não enxerga, porque o seletor já nem lista mais aquele tema.
    //
    // O GRI NÃO é zerado junto: ele não pertence ao pilar. "GRI 305-1" continua
    // sendo emissão de escopo 1 independentemente de onde a tela agrupou a
    // pergunta.
    onChange({ id_pillar: novoPilar, id_theme: null, gri_ids: griIds });
  };

  return (
    <Box
      borderWidth='1px'
      borderColor={erro ? 'red.300' : 'gray.200'}
      borderRadius='8px'
      bg='gray.50'
      p='16px'
      w='100%'
    >
      <Flex justify='space-between' align='baseline' mb='12px' gap='8px'>
        <Text fontSize='sm' fontWeight={600} color='gray.700'>
          Classificação da pergunta
        </Text>
        <Text fontSize='xs' color='gray.500'>
          Opcional
        </Text>
      </Flex>

      <Flex gap='16px' direction={{ base: 'column', md: 'row' }} w='100%'>
        <Box flex='1' minW='0'>
          <FormLabel htmlFor='id_pillar' fontSize='sm' mb='4px' color='gray.600'>
            Pilar
          </FormLabel>
          {/* isRequired invertido em relação ao default do PillarSelect: no
              catálogo o pilar é a chave, aqui classificar é opcional. */}
          <PillarSelect
            value={idPillar}
            onChange={trocarPilar}
            isRequired={false}
          />
        </Box>

        <Box flex='1' minW='0'>
          <FormLabel htmlFor='id_theme' fontSize='sm' mb='4px' color='gray.600'>
            Tema
          </FormLabel>
          {/* O filtro por pilar já vem pronto do ThemeSelect (CA02): ele
              seleciona por theme.id_pillar. Aqui só se acrescenta o CA03. */}
          <ThemeSelect
            idPillar={idPillar}
            value={idTheme}
            onChange={(novoTema) =>
              onChange({
                id_pillar: idPillar,
                id_theme: novoTema,
                gri_ids: griIds,
              })
            }
            isRequired={Boolean(idPillar)}
          />
        </Box>
      </Flex>

      {/* GRI-02 — CA01: dentro da MESMA seção, e não como campo solto. O
          indicador GRI é a terceira dimensão da mesma decisão ("o que esta
          pergunta mede"), e separá-lo sugeriria que é outro assunto.

          Em linha própria porque, ao contrário de pilar e tema, o valor aqui é
          uma lista: as tags do que foi escolhido crescem para baixo. */}
      <Box mt='16px'>
        <FormLabel fontSize='sm' mb='4px' color='gray.600'>
          Indicadores GRI
        </FormLabel>
        <GriMultiSelect
          value={griIds}
          onChange={(novosGri) =>
            onChange({ id_pillar: idPillar, id_theme: idTheme, gri_ids: novosGri })
          }
        />
        {/* As duas semânticas são opostas e o gestor não tem como adivinhar
            qual está valendo. No lote ele não vê os conjuntos das questões que
            vai tocar — dizer que soma é o que evita o susto de achar que
            apagou. */}
        {griSoma && (
          <Text fontSize='xs' color='gray.500' mt='6px'>
            Os indicadores escolhidos serão acrescentados às questões
            selecionadas, sem remover os que elas já têm.
          </Text>
        )}

        {/* EG-401: o conteúdo de cada indicador escolhido — nome, DESCRIÇÃO e
            unidade, e a prévia do campo como o respondente o verá. Sem isto o
            gestor classificava às cegas (só o código na tag) e tinha que
            publicar pra conferir. Vale nos três lugares que usam este
            componente: criar questão, editar no modal e classificar em massa. */}
        <MedidasGriComoFormulario griIds={griIds} />
      </Box>

      {erro && (
        <Text fontSize='sm' color='red.500' mt='8px'>
          {erro}
        </Text>
      )}

      {/* Slot para o checkbox de propagação: ele pertence visualmente a esta
          seção, mas só existe onde a questão já tem instâncias. */}
      {children && <Box mt='12px'>{children}</Box>}
    </Box>
  );
};

export default ClassificacaoQuestao;
